import { captureException } from '@atlassian/error-handling/sentry';

import { getActiveProducts } from '../active-products';
import { planProvisioning } from '../provisioning-api';
import type {
	ActivationCapability,
	Edition,
	ModalData,
	PersonalizationResponse,
	Pricing,
	ProductId,
	TapTrait,
} from './types';

export const isNullOrUndefined = <T>(value: T | null | undefined): value is null | undefined =>
	value === null || value === undefined;

const getActivationCapabilityFromReasons = (reasons?: string[]): ActivationCapability => {
	if (reasons?.some((reason) => reason === 'user-cannot-add-product')) {
		return 'requires-approval';
	}
	return 'can-activate';
};

export const fetchSiteTraits = async (cloudId: string): Promise<PersonalizationResponse | null> => {
	const response = await fetch(
		`/gateway/api/tap-delivery/api/v3/personalization/site/${encodeURIComponent(cloudId)}`,
		{
			method: 'GET',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'x-atlassian-mau-ignore': 'true',
			},
		},
	);

	if (!response.ok) {
		return null;
	}

	return response.json();
};

/**
 * Parse pricing segmentation from TAP site traits.
 * Extracted from the former getPriceIncreaseTraitValue so the trait response
 * can be shared with parseD0ActiveProducts (single fetch).
 */
export const parsePricingFromTraits = (response: PersonalizationResponse | null): Pricing => {
	if (!response) {
		return { segmentation: 'increase' };
	}

	const priceTrait = response.attributes.find(
		(trait: TapTrait) => trait.name === 'site-twc-price-percentage-increase',
	);
	if (!priceTrait) {
		return { segmentation: 'increase' };
	}

	if (!priceTrait.value) {
		return { segmentation: 'increase' };
	}

	const priceChangeValue = Number(priceTrait.value);
	if (!Number.isInteger(priceChangeValue)) {
		return { segmentation: 'increase' };
	}

	if (priceChangeValue < 0) {
		return { segmentation: 'decrease', value: priceChangeValue };
	} else if (priceChangeValue === 0) {
		return { segmentation: 'same', value: priceChangeValue };
	}

	return { segmentation: 'increase', value: priceChangeValue };
};

/**
 * ⚠️ EXHAUSTIVE Record<ProductId, string[] | null> — TypeScript will error if a new ProductId
 * is added without a corresponding entry. This is intentional.
 *
 * null means "provisioned = active" (no TAP trait needed).
 * string[] means the TAP trait names — if ANY trait in the array is present with a non-null,
 * non-empty value, the product is considered d0 active.
 * TAP returns a date string (e.g. "2024-11-15") when active, absent when not. Never a boolean.
 */
const D0_TRAIT_MAP: Record<ProductId, string[] | null> = {
	confluence: ['site_has_active_confluence_use', 'confluence_ui_viewed_site_first_time'],
	loom: ['site_has_active_loom_use', 'loom_ui_viewed_site_first_time'],
	jira: null, // provisioned = active
	rovo: null, // provisioned = active
};

/**
 * Given the TAP site traits response and the list of provisioned products,
 * return the subset that are considered "d0 active".
 *
 * Products with a null trait list (jira, rovo) are always considered active
 * when provisioned. Products with a trait list require at least ONE of the
 * traits to be present with a non-null, non-empty value (a date string from TAP).
 *
 * ⚠️ Do NOT use === true — TAP values are date strings, not booleans.
 */
export const parseD0ActiveProducts = (
	traits: PersonalizationResponse | null,
	userProducts: Array<ProductId>,
): Array<ProductId> => {
	return userProducts.filter((product) => {
		const traitNames = D0_TRAIT_MAP[product];
		if (traitNames === null) {
			// No TAP trait needed — provisioned means active
			return true;
		}
		if (!traits) {
			// No traits response — cannot confirm d0 activity
			return false;
		}
		// TAP returns a date string when active, absent when not. Never a boolean.
		// ⚠️ Do NOT change to === true
		// Active if ANY of the trait names is present with a non-null, non-empty value.
		return traitNames.some((traitName) =>
			traits.attributes.some(
				(t: TapTrait) => t.name === traitName && t.value != null && t.value !== '',
			),
		);
	});
};

export const fetchModalData = async (
	cloudId: string,
	sourceComponent: string,
	sourceContext: string,
	requestedEditionId?: Edition,
	attribution?: { touchpointName?: string; upgradeSourceProduct?: 'jira' | 'confluence' },
): Promise<ModalData> => {
	const [userProducts, planResponse, siteTraits] = await Promise.all([
		getActiveProducts(cloudId),
		planProvisioning({
			cloudId,
			requestedEditionId,
			sourceComponent,
			sourceContext,
			...attribution,
		}),
		fetchSiteTraits(cloudId).catch((error: unknown) => {
			// TAP is non-critical — a network failure falls back to safe defaults rather than
			// crashing fetchModalData. We still capture the exception for observability.
			captureException('[Handled] fetchSiteTraits network error', error as Error, {
				cloudId,
				packageName: '@atlassian/twc-xflow-modal',
				teamName: 'starburst',
				handled: 'true',
				defaultValue: 'null traits response',
				userImpact: 'none',
			});
			return null;
		}), // single fetch — shared between parsePricingFromTraits and parseD0ActiveProducts
	]);

	let pricing: Pricing;
	let d0ActiveProducts: Array<ProductId>;
	try {
		pricing = parsePricingFromTraits(siteTraits);
		d0ActiveProducts = parseD0ActiveProducts(siteTraits, userProducts);
	} catch (error) {
		captureException('[Handled] Failed to fetch or parse site traits', error as Error, {
			cloudId,
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
			handled: 'true',
			defaultValue: 'price increase segmentation',
			userImpact: 'none',
		});
		pricing = { segmentation: 'increase' };
		d0ActiveProducts = userProducts;
	}

	const allowed = planResponse.allowed;

	// additional logging to debug "Provisioning not allowed" error screen
	if (!allowed) {
		captureException(
			'response logging for not allowed',
			new Error('Provisioning not allowed') as Error,
			{
				cloudId,
				packageName: '@atlassian/twc-xflow-modal',
				teamName: 'starburst',
				userProducts: JSON.stringify(userProducts),
				planResponse: JSON.stringify(planResponse),
				pricing: JSON.stringify(pricing),
			},
		);
	}

	const reasons = !allowed ? planResponse.reasons : undefined;

	const inProgress =
		reasons?.some((reason: string) => reason === 'Job is already running') ?? false;

	const provisioningCompleted =
		reasons?.some((reason: string) => reason === 'product-already-active') ?? false;

	const jobLevelContext = !allowed && 'context' in planResponse ? planResponse.context : undefined;
	const status = provisioningCompleted ? 'PROVISIONING_COMPLETED' : jobLevelContext?.status; // future behavior: xflow will only return status if the job is running

	const hasPaymentDetails = planResponse.tasks?.[0]?.context?.hasPaymentDetails;
	if (allowed && isNullOrUndefined(hasPaymentDetails)) {
		throw new Error('Missing hasPaymentDetails from plan endpoint');
	}

	const edition = planResponse.tasks?.[0]?.context?.edition?.id;
	if (allowed && isNullOrUndefined(edition)) {
		throw new Error('Missing edition from plan endpoint');
	}

	const activationCapability = getActivationCapabilityFromReasons(reasons);

	return {
		modalData: {
			edition,
			hasPaymentDetails,
			userProducts,
			d0ActiveProducts,
			status,
			priceSegmentation: pricing.segmentation,
			priceValue: pricing.value,
			activationCapability,
		},
		allowed,
		inProgress,
	};
};
